import { Icon } from '@iconify/react';
import { useQueries, useQuery } from '@tanstack/react-query';
/**
 * Is anything waiting for me?
 *
 * That is the whole screen, and until now it was the one question Home did
 * not answer. It opened with four tiles reading 0, 0, 0 and 3/3, then a spend
 * card saying "no spend tracked yet", then a grid of everyone in the house
 * doing nothing in particular — a dashboard, of the kind that is looked at
 * once and never again. Meanwhile the things that genuinely needed him lived
 * elsewhere: a cash request had no screen at all, a late task had no button,
 * and the photograph the housekeeper took to prove the room was clean was in
 * the database and on no page.
 *
 * So: one line saying how many things need him, then those things, each one
 * finishable without leaving. When there is nothing, the queue disappears and
 * the screen says who is working and what is coming. Zero is silence, not a
 * tile with a nought in it.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ApiError,
  type Operation,
  type StaffRow,
  api,
  fmtMoney,
  fmtRelative,
  setCurrency,
} from '../api';
import markOrange from '../assets/brand/mark-orange.svg';
import { opPhoto, receiptPhoto } from '../assets/opPhotos';
import { Avatar, useAvatarUrl } from '../components/Avatar';
import { EnableNotifications } from '../components/EnableNotifications';
import { RuleSheet } from '../components/HouseRules';
import { PromptModal } from '../components/Modal';
import { NotificationBell } from '../components/NotificationBell';
import { toastError } from '../components/Toasts';
import { SkeletonScreen } from '../components/ui/Skeleton';
import { pickTranslation, t } from '../i18n';
import { get, invalidate } from '../query';
import { DailyReportEntry } from './DailyReport';
import { NoShowCard, ReassignChips, useSupervisionFlag, useTodayNoShows } from './Supervision';

interface ReceiptProposalRow {
  id: string;
  aiSummary: string | null;
  proposal:
    | { kind: 'task'; title: string; description?: string | null; due_at?: string | null }
    | {
        kind: 'routine';
        title: string;
        description?: string | null;
        schedule_rrule: string;
        schedule_tz: string;
      }
    | null;
  uploadedByUserId: string;
  updatedAt: string;
}

interface PulseItem {
  id: string;
  kind: 'operation' | 'routine' | 'calendar';
  title: string;
  title_translations?: Record<string, string> | null;
  when: string;
  state?: string;
  assignee_user_id?: string | null;
}

/** The server row, camelCase off drizzle. `state` is the enum in money.ts:
 *  requested → approved → dispensed → reconciled. Nothing in the web app has
 *  ever read anything but `dispensed`. */
interface CashRequest {
  id: string;
  requestedByUserId: string;
  amountMinor: number;
  currency: string;
  purpose: string | null;
  state: 'requested' | 'approved' | 'rejected' | 'dispensed' | 'reconciled';
  createdAt: string;
}

interface Reimbursement {
  id: string;
  staffUserId: string;
  amountMinor: number;
  currency: string;
  note: string | null;
  state: 'submitted' | 'approved' | 'rejected' | 'paid';
  createdAt: string;
}

/** `text` is written by the detector and has always been on the row; this
 *  screen used to ignore it and switch on `kind` instead — against three kinds
 *  the server never emits, so every suggestion rendered as a raw enum. */
interface Suggestion {
  id: string;
  kind: string;
  text: string | null;
  payload: Record<string, unknown>;
  // Matches the server's actual (camelCase, drizzle) response shape — the
  // route has no explicit response schema, so Fastify serializes the raw
  // row as-is. This field was previously typed as snake_case `created_at`
  // and missing `applyJsonb` entirely, which is why the apply flow (this
  // file's SuggestionCard) had nothing to apply: the type never exposed
  // the data it needed.
  applyJsonb?: { flow: string; seed: Record<string, unknown> } | null;
  createdAt?: string;
}

interface LatestProof {
  operation_id: string;
  type: string;
  url: string | null;
  text_body: string | null;
  created_at: string | null;
}

interface Household {
  id: string;
  name: string;
  household_code: string;
  primary_currency: string;
  profile: { onboarded_at?: string };
}

interface MonthlyExpensesSnap {
  currency: string;
  total_minor: number;
  recent: Array<{ id: string }>;
}

/** Subset of /v1/me — the greeting's first name and the header avatar chip.
 *  The same query StaffToday runs; the cache makes the second call free. */
interface MeRow {
  id: string;
  role: 'owner' | 'staff';
  full_name: string;
  avatar_r2_key: string | null;
}

/* The design's two button pills, shared by every decision card: Approve is
   the signature orange with the orange glow, Reject the quiet grey fill. */
const PILL_PRIMARY =
  'h-12 flex-1 rounded-full bg-primary text-[13px] font-bold tracking-wide text-white shadow-lg shadow-primary/20 transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100';
const PILL_QUIET =
  'h-12 flex-1 rounded-full bg-secondary text-[13px] font-bold tracking-wide text-foreground transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100';

/* The white plate every card on this screen is drawn on — 24px corners, a
   warm hairline, and a shadow you have to look for. */
const PLATE =
  'flex flex-col gap-5 rounded-[24px] border border-border bg-card p-6 shadow-[0_8px_30px_rgba(0,0,0,0.02)]';

export function OwnerHome() {
  const nav = useNavigate();
  // The suggestion card whose "write the rule" the owner clicked — the
  // sheet opens seeded from that suggestion's apply.seed, mirrors the
  // mobile app's ruleSeed (apps/mobile/app/(owner)/home.tsx).
  const [ruleSeed, setRuleSeed] = useState<{
    suggestionId: string;
    category: string | null;
  } | null>(null);
  // Computed once per mount, not per render — react-query's cache/staleTime
  // keys off this exact string, and a value that changes every render (this
  // used to build straight off `new Date()` in the component body,
  // millisecond-precise) means every render mints a query the cache has
  // never seen, so it refetches immediately. Since every resolved fetch
  // itself triggers a re-render, that turned into a continuous refetch loop
  // on the single highest-traffic screen in the app. "Now" only needs to be
  // roughly current for a timeline widget — stable for as long as the
  // screen stays mounted is the right freshness, not millisecond-exact.
  const { nowIso, pulseKey } = useMemo(() => {
    const now = new Date();
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);
    return {
      nowIso: now,
      pulseKey: `/v1/pulse?from=${encodeURIComponent(now.toISOString())}&to=${encodeURIComponent(endOfDay.toISOString())}`,
    };
  }, []);

  /**
   * Ten answers, each on its own.
   *
   * This used to be one `Promise.allSettled` behind a single `loaded` flag, so
   * a slow receipts call held the whole morning queue back and a failing one
   * was reported as the screen having failed. They are ten questions now: the
   * queue draws the moment the queue is known, and a section whose answer did
   * not arrive is simply the section that is missing.
   */
  const [opsQ, staffQ, pulseQ, sugQ, hhQ, rcptQ, cashQ, cashOkQ, reimbQ, monthQ] = useQueries({
    queries: [
      // No `tab` param — the server's "everything" view. Every task that
      // isn't yet approved/cancelled comes back in full no matter how old its
      // updated_at is, so a task scheduled for next week can't be silently
      // pushed out of the window by a busy day of approvals. Only the
      // approved/cancelled history is paginated (next_cursor/has_more); this
      // screen only reads recent completions off the digest, so it doesn't
      // page through history here.
      {
        queryKey: ['/v1/operations'],
        queryFn: get<{ items: Operation[]; next_cursor: string | null; has_more: boolean }>,
        refetchInterval: 30_000,
      },
      { queryKey: ['/v1/staff'], queryFn: get<{ items: StaffRow[] }> },
      { queryKey: [pulseKey], queryFn: get<{ items: PulseItem[] }> },
      { queryKey: ['/v1/suggestions'], queryFn: get<{ items: Suggestion[] }> },
      { queryKey: ['/v1/household'], queryFn: get<{ household: Household }> },
      { queryKey: ['/v1/receipts'], queryFn: get<{ items: ReceiptProposalRow[] }> },
      // Two states, two calls: money asked for in the shop, and money he has
      // already said yes to but not yet handed over. Both are things waiting
      // on him; neither had a screen.
      { queryKey: ['/v1/cash-requests?state=requested'], queryFn: get<{ items: CashRequest[] }> },
      { queryKey: ['/v1/cash-requests?state=approved'], queryFn: get<{ items: CashRequest[] }> },
      {
        queryKey: ['/v1/reimbursements?state=submitted'],
        queryFn: get<{ items: Reimbursement[] }>,
      },
      { queryKey: ['/v1/expenses/monthly'], queryFn: get<MonthlyExpensesSnap> },
    ],
  });

  // The greeting's name and the header's avatar chip — the same /v1/me every
  // other screen asks for.
  const meQ = useQuery({ queryKey: ['/v1/me'], queryFn: get<{ user: MeRow }> });
  const me = meQ.data?.user ?? null;

  const ops = opsQ.data?.items ?? [];
  const staff = staffQ.data?.items ?? [];
  const pulse = pulseQ.data?.items ?? [];
  const suggestions = sugQ.data?.items ?? [];
  const household = hhQ.data?.household ?? null;
  const receipts = rcptQ.data?.items ?? [];
  const cash = [...(cashQ.data?.items ?? []), ...(cashOkQ.data?.items ?? [])];
  const reimbursements = reimbQ.data?.items ?? [];
  const monthly = monthQ.data ?? null;
  const loaded = !opsQ.isPending && !staffQ.isPending;
  const err = (() => {
    const e = opsQ.error ?? staffQ.error;
    if (!e) return null;
    if (e instanceof ApiError) {
      // Infrastructure chatter (rate limits, 5xx) is never rendered raw on
      // the main screen — the household content stays up, the retry is calm.
      if (e.status === 429) return t('The house is busy — back in a moment.');
      if (e.status >= 500) return t('Loading failed');
      return e.message;
    }
    return t('Loading failed');
  })();
  const refreshedAt = opsQ.dataUpdatedAt
    ? new Date(opsQ.dataUpdatedAt).toISOString()
    : nowIso.toISOString();

  // The photograph is the decision. One call for the whole queue rather than
  // one per card — and a failure here costs a thumbnail, not a row.
  const waitingIds = ops
    .filter((o) => o.state === 'waiting_approval')
    .slice(0, 20)
    .map((o) => o.id);
  const proofsQ = useQueries({
    queries: [
      {
        queryKey: [`/v1/operations/proofs/latest?ids=${waitingIds.join(',')}`],
        queryFn: get<{ items: LatestProof[] }>,
        enabled: waitingIds.length > 0,
      },
    ],
  })[0];
  const proofs = new Map((proofsQ?.data?.items ?? []).map((p) => [p.operation_id, p]));

  // Somebody who has not finished setting the house up has no queue to read.
  useEffect(() => {
    if (household && !household.profile?.onboarded_at) nav('/onboarding', { replace: true });
    if (household) setCurrency(household.primary_currency);
  }, [household, nav]);

  const load = () =>
    invalidate(
      '/v1/operations',
      '/v1/staff',
      '/v1/receipts',
      '/v1/cash-requests',
      '/v1/reimbursements',
      '/v1/suggestions',
      '/v1/expenses',
    );

  const staffById = new Map(staff.map((s) => [s.id, s]));
  const awaitingApproval = ops.filter((o) => o.state === 'waiting_approval');
  const late = ops.filter((o) => o.state === 'overdue' || o.state === 'escalated');
  const running = ops.filter((o) => o.state === 'in_progress' || o.state === 'waiting_proof');
  const activeStaff = staff.filter((s) => !s.disabled_at);

  // The supervision half of "needs attention": shifts nobody clocked in for.
  // The flag gates the fetch itself — dark everywhere until the household
  // turns supervision on, the same discipline the endpoint enforces.
  const supervisionOn = useSupervisionFlag();
  const noShows = useTodayNoShows(supervisionOn);

  /**
   * Every piece of work that is out there, most-alive first.
   *
   * This used to be one row per person built from `in_progress` alone, which
   * meant a house with four tasks sent to three people and nobody having
   * pressed Start yet — the ordinary state of a morning — showed a blank
   * screen under a status line saying all was under control. Sent, seen,
   * waiting for proof and scheduled are all work that is out there, and the
   * design's rows are per task, not per person, so each one gets its row.
   */
  // Draft is in the list for the same reason the others are: it is work the
  // owner created that has not happened. A task typed with nobody assigned is
  // born draft, and leaving it out of every list is how one disappears the
  // moment it is written. It ranks first — it is the only state waiting on the
  // owner rather than on anyone else.
  const OPEN: string[] = ['draft', 'sent', 'seen', 'in_progress', 'waiting_proof', 'scheduled'];
  const RANK: Record<string, number> = {
    draft: -1,
    in_progress: 0,
    waiting_proof: 1,
    seen: 2,
    sent: 3,
    scheduled: 4,
  };
  const openOps = ops
    .filter((o) => OPEN.includes(o.state))
    .sort((a, b) => (RANK[a.state] ?? 9) - (RANK[b.state] ?? 9));

  const decisions = awaitingApproval.length + cash.length + receipts.length + reimbursements.length;
  const waitingCount = decisions + late.length;
  const isEmpty = staff.length === 0 && ops.length === 0;

  if (!loaded) return <SkeletonScreen rows={4} />;

  return (
    // A phone column, centred; on a laptop it opens into two columns — the
    // queue on one side, the day on the other — because stretched to a wide
    // screen a single stack of cards reads as a feed, not a morning.
    <div className="mx-auto w-full max-w-lg lg:max-w-[1200px]">
      <HomeHeader household={household} me={me} />
      <Greeting
        me={me}
        waiting={waitingCount}
        working={running.length}
        staffCount={activeStaff.length}
        next={pulse[0]}
        refreshedAt={refreshedAt}
      />

      {err && (
        <div className="mb-10 rounded-[24px] border border-urgent/10 bg-urgent/10 p-6 text-urgent">
          {err}
          <button
            type="button"
            onClick={() => void load()}
            className="mt-4 block rounded-2xl bg-urgent/10 px-6 py-3 text-xs font-bold uppercase tracking-widest text-urgent transition-colors active:bg-urgent/20"
          >
            {t('Tap to retry')}
          </button>
        </div>
      )}

      <EnableNotifications />
      {isEmpty && <Quickstart />}

      <div className="lg:grid lg:grid-cols-2 lg:items-start lg:gap-12">
        <div className="flex flex-col gap-12">
          {decisions > 0 && (
            <section className="flex flex-col gap-5">
              <h2 className="section-title mb-0 px-1">{t('Requires decision')}</h2>
              <div className="flex flex-col gap-4">
                {awaitingApproval.map((op) => (
                  <ApprovalCard
                    key={op.id}
                    op={op}
                    proof={proofs.get(op.id) ?? null}
                    staffById={staffById}
                    onChange={() => void load()}
                  />
                ))}

                {cash.map((c) => (
                  <CashCard key={c.id} row={c} staffById={staffById} onChange={() => void load()} />
                ))}

                {receipts.map((r) => (
                  <ReceiptCard
                    key={r.id}
                    row={r}
                    staffById={staffById}
                    onChange={() => void load()}
                  />
                ))}

                {reimbursements.map((r) => (
                  <ReimbursementCard
                    key={r.id}
                    row={r}
                    staffById={staffById}
                    onChange={() => void load()}
                  />
                ))}
              </div>
            </section>
          )}

          {(late.length > 0 || noShows.length > 0) && (
            <section className="flex flex-col gap-5">
              <h2 className="section-title mb-0 px-1">{t('Needs attention')}</h2>
              <div className="flex flex-col gap-4">
                {noShows.map((shift) => (
                  <NoShowCard
                    key={shift.id}
                    shift={shift}
                    staff={activeStaff}
                    onChange={() => void load()}
                  />
                ))}
                {late.map((op) => (
                  <LateCard
                    key={op.id}
                    op={op}
                    staffById={staffById}
                    staff={activeStaff}
                    supervisionOn={supervisionOn}
                    onChange={() => void load()}
                  />
                ))}
              </div>
            </section>
          )}

          {suggestions.length > 0 && (
            <section className="flex flex-col gap-5">
              <h2 className="section-title mb-0 px-1">{t('Worth a look')}</h2>
              <div className="flex flex-col gap-4">
                {suggestions.map((s) => (
                  <SuggestionCard
                    key={s.id}
                    s={s}
                    onChange={() => void load()}
                    onOpenMemory={() =>
                      setRuleSeed({
                        suggestionId: s.id,
                        category:
                          typeof s.applyJsonb?.seed?.category === 'string'
                            ? s.applyJsonb.seed.category
                            : null,
                      })
                    }
                  />
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="mt-12 flex flex-col gap-12 lg:mt-0">
          {openOps.length > 0 && (
            <section className="flex flex-col gap-4">
              <h2 className="section-title mb-0 px-1">
                {running.length > 0 ? t('Running now') : t('Today')}
              </h2>
              <div className="flex flex-col gap-3">
                {openOps.map((op) => (
                  <RunningRow
                    key={op.id}
                    op={op}
                    person={op.assigneeUserId ? (staffById.get(op.assigneeUserId) ?? null) : null}
                  />
                ))}
              </div>
            </section>
          )}

          {pulse.length > 0 && (
            <section className="flex flex-col gap-6">
              <h2 className="section-title mb-0 px-1">{t('Up next')}</h2>
              <div className="ms-2 flex flex-col gap-8">
                {pulse.slice(0, 8).map((item, i, all) => (
                  <TimelineRow
                    key={`${item.kind}-${item.id}`}
                    item={item}
                    isNext={i === 0}
                    isLast={i === all.length - 1}
                    staffById={staffById}
                  />
                ))}
              </div>
            </section>
          )}

          <QuietLinks monthly={monthly} />
        </div>
      </div>
      {ruleSeed && (
        <RuleSheet
          entry={null}
          seedCategory={ruleSeed.category ?? undefined}
          onClose={() => setRuleSeed(null)}
          onSaved={() => {
            // The rule exists — stamp the suggestion applied so it leaves
            // the list. A failed stamp only means the card lingers; the
            // rule (the thing that mattered) is already saved.
            void api(`/v1/suggestions/${ruleSeed.suggestionId}/apply`, {
              method: 'POST',
              body: '{}',
            }).catch(() => {});
            setRuleSeed(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

/**
 * The three chips across the top of the phone — the house, the bell, him.
 * Mobile only: a laptop keeps the shell's own bar, with the same bell and
 * profile in it.
 */
function HomeHeader({ household, me }: { household: Household | null; me: MeRow | null }) {
  return (
    <header className="flex items-center justify-between pb-4 sm:hidden">
      <Link
        to="/memory"
        aria-label={household?.name ?? t('My household')}
        className="flex h-12 w-12 items-center justify-center rounded-[20px] border border-border bg-card text-foreground shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all active:scale-95"
      >
        <img src={markOrange} alt="" className="h-6 w-6 object-contain" aria-hidden />
      </Link>
      <div className="flex items-center gap-3">
        <DailyReportEntry variant="chip" />
        <NotificationBell variant="chip" />
        <OwnerAvatarChip me={me} />
      </div>
    </header>
  );
}

/** His photograph in the design's puffy white chip, initials until there is
 *  one. Links to the profile screen, as the old round button did. */
function OwnerAvatarChip({ me }: { me: MeRow | null }) {
  const url = useAvatarUrl(me?.id);
  const name = me?.full_name ?? '';
  const initials =
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => Array.from(w)[0]?.toLocaleUpperCase() ?? '')
      .join('') || '?';
  return (
    <Link
      to="/profile"
      aria-label={t('Profile')}
      className="flex h-12 w-12 items-center justify-center rounded-[20px] border border-border bg-card p-1 shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all active:scale-95"
    >
      {url ? (
        <img src={url} alt={name} className="h-full w-full rounded-[16px] object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center rounded-[16px] bg-secondary text-sm font-bold text-foreground">
          {initials}
        </span>
      )}
    </Link>
  );
}

function timeOfDayGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return t('Good morning');
  if (h < 18) return t('Good afternoon');
  return t('Good evening');
}

/**
 * The biggest type on the screen says good morning by name; the line under it
 * is the whole screen in one sentence. A number he can act on gets the orange;
 * a quiet house says so and stops talking.
 */
function Greeting({
  me,
  waiting,
  working,
  staffCount,
  next,
  refreshedAt,
}: {
  me: MeRow | null;
  waiting: number;
  working: number;
  staffCount: number;
  next?: PulseItem;
  refreshedAt: string;
}) {
  const loud = waiting > 0;
  const firstName = me?.full_name.trim().split(/\s+/)[0] ?? null;
  return (
    <section className="pb-14 pt-8 sm:pt-2">
      <h1 className="mb-4 text-[38px] font-semibold leading-[1.05] tracking-[-0.05em] text-foreground">
        {timeOfDayGreeting()},
        {firstName && (
          <>
            <br />
            <span dir="auto">{firstName}.</span>
          </>
        )}
      </h1>
      <p className="text-[13px] font-medium leading-relaxed tracking-tight text-muted-foreground">
        {loud ? (
          <span className="font-bold text-primary">
            {waiting === 1
              ? t('One thing needs you')
              : t('{{n}} things need you').replace('{{n}}', String(waiting))}
          </span>
        ) : (
          <span className="font-bold text-foreground">{t('All under control.')}</span>
        )}
        {' · '}
        {working > 0
          ? t('{{n}} running now').replace('{{n}}', String(working))
          : t('{{n}} people').replace('{{n}}', String(staffCount))}
        <br />
        {next ? (
          <span dir="auto">
            {t('Next')}: {pickTranslation(next.title_translations, next.title)}
          </span>
        ) : (
          fmtRelative(refreshedAt)
        )}
      </p>
    </section>
  );
}

/** The tiny photograph inside a subline — the design dots a 20px grayscale
 *  face next to a name wherever a person is mentioned in passing. */
function MiniAvatar({ name, userId }: { name: string; userId: string }) {
  const url = useAvatarUrl(userId);
  const initial = Array.from(name.trim())[0]?.toLocaleUpperCase() ?? '?';
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className="h-5 w-5 shrink-0 rounded-full object-cover opacity-60 grayscale"
      />
    );
  }
  return (
    <span
      aria-hidden
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-[9px] font-bold text-muted-foreground"
    >
      {initial}
    </span>
  );
}

/** The head of a money card: the receipt photograph at its tipped angle, the
 *  amount and what it was for, and who is waiting for it. The amount gets its
 *  own LTR isolate — fmtMoney formats in the account's language, and in he/ur
 *  that string carries RTL marks that would otherwise scatter the digits
 *  across the title. */
function MoneyCardHead({
  amount,
  title,
  subline,
  person,
}: {
  amount?: string;
  title: string;
  subline: string;
  person: StaffRow | null;
}) {
  return (
    <div className="flex items-center gap-4">
      <div className="h-12 w-12 shrink-0 rotate-[-4deg] overflow-hidden rounded-xl border border-border shadow-sm">
        <img src={receiptPhoto} alt={t('Receipt')} className="h-full w-full object-cover" />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <h3 className="text-[18px] font-semibold leading-[1.3] tracking-tight text-foreground">
          {amount && <span dir="ltr">{amount}</span>}
          {amount && title && ' — '}
          {title && <span dir="auto">{title}</span>}
        </h3>
        <div className="mt-1 flex items-center gap-2">
          {person && <MiniAvatar name={person.full_name} userId={person.id} />}
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
            {subline}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The finished room, on the card.
 *
 * The proof has existed on every one of these tasks and been visible on none
 * of them — the links the app built pointed at `/r2/<key>`, which is not an
 * address. Approving a thing you cannot see is a rubber stamp, which is the
 * one thing this product exists to stop being.
 */
function ApprovalCard({
  op,
  proof,
  staffById,
  onChange,
}: {
  op: Operation;
  proof: LatestProof | null;
  staffById: Map<string, StaffRow>;
  onChange: () => void;
}) {
  const person = op.assigneeUserId ? staffById.get(op.assigneeUserId) : null;
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function decide(to: 'approved' | 'rejected', reason?: string) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/operations/${op.id}/transitions/${to}`, {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      });
      onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
      throw e;
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`${PLATE} transition-transform active:scale-[0.99]`}>
      <div className="flex items-center gap-2.5">
        {person && (
          <Avatar
            name={person.full_name}
            userId={person.id}
            size="xs"
            className="border border-black/[0.05]"
          />
        )}
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
          {person
            ? `${person.full_name} · ${fmtRelative(op.updatedAt ?? op.createdAt ?? '')}`
            : fmtRelative(op.updatedAt ?? op.createdAt ?? '')}
        </span>
      </div>
      <h3 className="text-[18px] font-semibold leading-[1.3] tracking-tight text-foreground">
        <Link to={`/op/${op.id}`} dir="auto">
          {pickTranslation(op.titleTranslations, op.title)}
        </Link>
      </h3>

      {proof?.url && proof.type === 'photo' && (
        <Link
          to={`/op/${op.id}`}
          className="relative block aspect-[16/10] w-full overflow-hidden rounded-[18px] border border-border bg-muted transition-transform active:scale-[0.98]"
        >
          <img src={proof.url} alt="" className="h-full w-full object-cover" />
        </Link>
      )}
      {proof?.url && proof.type === 'voice' && (
        <audio controls src={proof.url} className="w-full">
          <track kind="captions" />
        </audio>
      )}
      {proof?.text_body && (
        <p className="text-xs font-medium italic leading-relaxed text-foreground/80" dir="auto">
          “{proof.text_body}”
        </p>
      )}

      {err && <p className="text-xs font-bold text-urgent">{err}</p>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          className={PILL_PRIMARY}
          disabled={busy}
          onClick={() => void decide('approved')}
        >
          {t('Approve')}
        </button>
        <button
          type="button"
          className={PILL_QUIET}
          disabled={busy}
          onClick={() => setRejecting(true)}
        >
          {t('Reject')}
        </button>
      </div>

      {rejecting && (
        <PromptModal
          title={t('Why reject?')}
          placeholder={t('Optional context for the team')}
          required={false}
          onSubmit={(reason: string) => decide('rejected', reason)}
          onClose={() => setRejecting(false)}
        />
      )}
    </div>
  );
}

/**
 * Money asked for in the shop.
 *
 * The server has had `requested → approved → dispensed` since the beginning
 * and the app only ever fetched `dispensed` — a list of money already gone.
 * There has been no way, anywhere, to answer somebody standing at a till.
 */
function CashCard({
  row,
  staffById,
  onChange,
}: {
  row: CashRequest;
  staffById: Map<string, StaffRow>;
  onChange: () => void;
}) {
  const person = staffById.get(row.requestedByUserId) ?? null;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);

  async function decide(decision: 'approve' | 'dispense' | 'reject', note?: string) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/cash-requests/${row.id}/decisions/${decision}`, {
        method: 'POST',
        body: JSON.stringify(note ? { note } : {}),
      });
      onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
      throw e;
    } finally {
      setBusy(false);
    }
  }

  // Already approved means the decision is made and the notes are not yet in
  // their hand — one button, and it says what it does.
  const approved = row.state === 'approved';

  return (
    <div className={PLATE}>
      <MoneyCardHead
        amount={fmtMoney(row.amountMinor, row.currency)}
        title={row.purpose ?? ''}
        subline={`${t('Cash request')} · ${person?.full_name ?? t('Someone')}`}
        person={person}
      />

      {err && <p className="text-xs font-bold text-urgent">{err}</p>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          className={PILL_PRIMARY}
          disabled={busy}
          onClick={() => void decide(approved ? 'dispense' : 'approve')}
        >
          {approved ? t('Mark as handed over') : t('Approve & dispense')}
        </button>
        <button
          type="button"
          className={PILL_QUIET}
          disabled={busy}
          onClick={() => setRejecting(true)}
        >
          {t('Reject')}
        </button>
      </div>

      {rejecting && (
        <PromptModal
          title={t('Why reject?')}
          placeholder={t('Optional context for the team')}
          required={false}
          onSubmit={(note: string) => decide('reject', note)}
          onClose={() => setRejecting(false)}
        />
      )}
    </div>
  );
}

function ReceiptCard({
  row,
  staffById,
  onChange,
}: {
  row: ReceiptProposalRow;
  staffById: Map<string, StaffRow>;
  onChange: () => void;
}) {
  const person = staffById.get(row.uploadedByUserId) ?? null;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);

  async function decide(decision: 'approve' | 'reject', reason?: string) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/receipts/${row.id}/${decision}`, {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      });
      onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
      throw e;
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={PLATE}>
      <MoneyCardHead
        title={row.proposal?.title ?? t('(empty proposal)')}
        subline={`${t('Receipt')} · ${person?.full_name ?? t('Someone')}`}
        person={person}
      />

      {row.aiSummary && (
        <p className="text-xs font-medium leading-relaxed text-muted-foreground" dir="auto">
          {row.aiSummary}
        </p>
      )}

      {err && <p className="text-xs font-bold text-urgent">{err}</p>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          className={PILL_PRIMARY}
          disabled={busy}
          onClick={() => void decide('approve')}
        >
          {t('Approve')}
        </button>
        <button
          type="button"
          className={PILL_QUIET}
          disabled={busy}
          onClick={() => setRejecting(true)}
        >
          {t('Reject')}
        </button>
      </div>

      {rejecting && (
        <PromptModal
          title={t('Why reject?')}
          placeholder={t('Optional context for the team')}
          required={false}
          onSubmit={(reason: string) => decide('reject', reason)}
          onClose={() => setRejecting(false)}
        />
      )}
    </div>
  );
}

function ReimbursementCard({
  row,
  staffById,
  onChange,
}: {
  row: Reimbursement;
  staffById: Map<string, StaffRow>;
  onChange: () => void;
}) {
  const person = staffById.get(row.staffUserId) ?? null;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);

  async function decide(decision: 'approve' | 'reject', note?: string) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/reimbursements/${row.id}/decisions/${decision}`, {
        method: 'POST',
        body: JSON.stringify(note ? { note } : {}),
      });
      onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
      throw e;
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={PLATE}>
      <MoneyCardHead
        amount={fmtMoney(row.amountMinor, row.currency)}
        title={row.note ?? ''}
        subline={`${t('Paid out of pocket')} · ${person?.full_name ?? t('Someone')}`}
        person={person}
      />

      {err && <p className="text-xs font-bold text-urgent">{err}</p>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          className={PILL_PRIMARY}
          disabled={busy}
          onClick={() => void decide('approve')}
        >
          {t('Approve')}
        </button>
        <button
          type="button"
          className={PILL_QUIET}
          disabled={busy}
          onClick={() => setRejecting(true)}
        >
          {t('Reject')}
        </button>
      </div>

      {rejecting && (
        <PromptModal
          title={t('Why reject?')}
          placeholder={t('Optional context for the team')}
          required={false}
          onSubmit={(note: string) => decide('reject', note)}
          onClose={() => setRejecting(false)}
        />
      )}
    </div>
  );
}

/**
 * Late, with something to press.
 *
 * The app has known a task was late for a while — the worker marks it and
 * tells the person holding it. What it never had was a way for him to say
 * "any news?" without leaving for WhatsApp, which is the habit this product
 * exists to replace.
 */
function LateCard({
  op,
  staffById,
  staff,
  supervisionOn,
  onChange,
}: {
  op: Operation;
  staffById: Map<string, StaffRow>;
  staff: StaffRow[];
  supervisionOn: boolean;
  onChange: () => void;
}) {
  const person = op.assigneeUserId ? staffById.get(op.assigneeUserId) : null;
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // The reassign half of the suggested action below: closed, it is one quiet
  // line; open, the other staff are one tap each. The toggle exists because
  // the card already has a primary action (nudge) and two primaries shouting
  // on one plate is how a morning queue becomes noise.
  const [handing, setHanding] = useState(false);
  const [reassignBusyId, setReassignBusyId] = useState<string | null>(null);

  async function nudge() {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/operations/${op.id}/nudge`, { method: 'POST', body: '{}' });
      setNote(t('They have been reminded.'));
      onChange();
    } catch (e) {
      // "We told them a minute ago" is not a failure, and it is usually the
      // lateness worker that told them — it stamps the same clock the moment
      // it marks a task late, which is exactly when he opens the app. Saying
      // it in red made the app look broken for doing the right thing.
      if (e instanceof ApiError && e.code === 'NUDGED_RECENTLY') {
        setNote(e.message);
      } else {
        setErr(e instanceof ApiError ? e.message : t('Failed'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function reassignTo(next: StaffRow) {
    setReassignBusyId(next.id);
    setErr(null);
    try {
      await api(`/v1/operations/${op.id}/reassign`, {
        method: 'POST',
        body: JSON.stringify({ assignee_user_id: next.id }),
      });
      setNote(t('{{name}} has it now.').replace('{{name}}', next.full_name));
      setHanding(false);
      onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
    } finally {
      setReassignBusyId(null);
    }
  }

  const lateBy = op.dueAt ? fmtRelative(op.dueAt) : null;

  return (
    <div className={PLATE}>
      <div className="flex items-center gap-2.5">
        {person && (
          <Avatar
            name={person.full_name}
            userId={person.id}
            size="xs"
            className="border border-black/[0.05]"
          />
        )}
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-magic">
          {person && lateBy
            ? t('{{name}} is late — due {{when}}')
                .replace('{{name}}', person.full_name)
                .replace('{{when}}', lateBy)
            : t('Past its deadline')}
        </span>
      </div>
      <h3 className="text-[18px] font-semibold leading-[1.3] tracking-tight text-foreground">
        <Link to={`/op/${op.id}`} dir="auto">
          {pickTranslation(op.titleTranslations, op.title)}
        </Link>
      </h3>

      {op.state === 'escalated' && (
        <div className="rounded-2xl bg-magic/10 p-3">
          <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-magic">
            {t('Suggested action')}
          </p>
          <p className="text-xs font-medium leading-relaxed text-magic">
            {t('Nobody has picked this up. Ask for a status or hand it to someone else.')}
          </p>
        </div>
      )}

      {note && <p className="text-xs font-semibold text-muted-foreground">{note}</p>}
      {err && <p className="text-xs font-bold text-urgent">{err}</p>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="h-12 flex-1 rounded-full bg-magic text-[13px] font-bold tracking-wide text-white transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100"
          disabled={busy || !person}
          onClick={() => void nudge()}
        >
          {person ? t('Nudge {{name}}').replace('{{name}}', person.full_name) : t('Nudge')}
        </button>
        <Link
          to={`/op/${op.id}`}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground transition-all active:scale-[0.98]"
          aria-label={t('Open')}
        >
          <Icon icon="ph:chats-bold" className="text-xl" aria-hidden />
        </Link>
      </div>

      {/* Reassignment is the owner's call, never the app's — but it should be
          one tap away, not a trip through the edit form. supervision_v1 only:
          the endpoint refuses without it, so the card doesn't offer what the
          household hasn't turned on. */}
      {supervisionOn &&
        (handing ? (
          <div className="flex flex-col gap-3 border-t border-border pt-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
              {t('Hand it to')}
            </p>
            <ReassignChips
              people={staff.filter((s) => s.id !== op.assigneeUserId)}
              busyId={reassignBusyId}
              onPick={(p) => void reassignTo(p)}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setHanding(true)}
            className="flex items-center gap-2 self-start text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground transition-colors active:text-foreground"
          >
            <Icon icon="ph:arrows-left-right-bold" className="text-sm" aria-hidden />
            {t('Hand to someone else')}
          </button>
        ))}
    </div>
  );
}

/**
 * The detector's sentence says what happened; the kind says what sort of thing
 * it is. A title and an icon above the sentence is how "delegate work" or
 * "unusual spend" reads as a category rather than one more line of prose.
 * Unknown kinds fall back to the generic sparkle with no title.
 */
const SUGGESTION_META: Record<string, { icon: string; label: string }> = {
  unapproved_receipts: { icon: 'ph:receipt-bold', label: 'Receipts to review' },
  cash_lingering: { icon: 'ph:money-bold', label: 'Unreconciled cash' },
  loan_inactivity: { icon: 'ph:hand-coins-bold', label: 'Dormant loan' },
  unused_memory: { icon: 'ph:brain-bold', label: 'Unused note' },
  delegation_opportunity: { icon: 'ph:users-three-bold', label: 'Delegate work' },
  spend_anomaly: { icon: 'ph:chart-line-up-bold', label: 'Unusual spend' },
  starter_pack: { icon: 'ph:sparkle-fill', label: 'Good start' },
  person_onboarding: { icon: 'ph:user-plus-bold', label: 'New on the team' },
  // Wave 2 intelligence packs (apps/api src/packs) — the pack rhythm cards.
  dog_rhythm: { icon: 'ph:dog-bold', label: 'Dog care' },
  dog_food: { icon: 'ph:package-bold', label: 'Dog food' },
  child_rhythm: { icon: 'ph:backpack-bold', label: 'Kids' },
  vehicle_rhythm: { icon: 'ph:car-bold', label: 'Car care' },
  property_rhythm: { icon: 'ph:house-bold', label: 'Property upkeep' },
};

/**
 * Where a card's apply click takes the owner, per the detector's apply.flow
 * — the web routes for the same flows apps/mobile/app/(owner)/home.tsx's
 * flowRoute maps, collapsed onto web's fewer top-level screens (receipts,
 * bills, cash and loans all live on /money here, not separate screens).
 * 'open_memory' is NOT here — it opens RuleSheet instead. An unknown flow
 * is dismiss-only: never a dead button.
 */
function flowRoute(s: Suggestion): string | null {
  switch (s.applyJsonb?.flow) {
    case 'open_routines':
      return '/routines';
    case 'open_receipts':
    case 'open_receipt':
    case 'open_cash_request':
    case 'open_loan':
    case 'open_bills':
      return '/money';
    case 'open_operations':
      return '/tasks';
    case 'archive_memory':
      return '/rules';
    case 'person_onboarding': {
      const staffId = s.applyJsonb?.seed?.staff_id;
      return typeof staffId === 'string' && staffId ? `/staff/${staffId}` : '/staff';
    }
    default:
      return null;
  }
}

function SuggestionCard({
  s,
  onChange,
  onOpenMemory,
}: {
  s: Suggestion;
  onChange: () => void;
  onOpenMemory: () => void;
}) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const meta = SUGGESTION_META[s.kind];
  const flow = s.applyJsonb?.flow;
  const isMemoryFlow = flow === 'open_memory';
  const isPackFlow = flow === 'create_routine' || flow === 'mark_food_bought';
  const route = isMemoryFlow || isPackFlow ? null : flowRoute(s);
  const applyLabel = isMemoryFlow
    ? t('Write the rule')
    : flow === 'create_routine'
      ? t('Set it up')
      : flow === 'mark_food_bought'
        ? t('Mark bought')
        : t('Open');
  async function dismiss() {
    setBusy(true);
    try {
      await api(`/v1/suggestions/${s.id}/dismiss`, { method: 'POST', body: '{}' });
      onChange();
    } catch (e) {
      toastError(e);
      setBusy(false);
    }
  }
  async function apply() {
    setBusy(true);
    try {
      await api(`/v1/suggestions/${s.id}/apply`, { method: 'POST', body: '{}' });
      onChange();
      if (flow === 'create_routine') nav('/routines');
    } catch (e) {
      toastError(e);
      setBusy(false);
    }
  }
  return (
    <div className={`${PLATE} flex-row items-start gap-4`}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-secondary text-muted-foreground">
        <Icon icon={meta?.icon ?? 'ph:sparkle-fill'} className="text-lg" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        {meta && (
          <p className="mb-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
            {t(meta.label)}
          </p>
        )}
        {s.text && (
          <p className="text-sm font-medium leading-relaxed" dir="auto">
            {s.text}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {isMemoryFlow || isPackFlow || route ? (
          <button
            type="button"
            className="flex min-h-11 items-center rounded-full bg-primary px-4 text-[11px] font-bold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-40"
            disabled={busy}
            onClick={() => {
              if (isMemoryFlow) onOpenMemory();
              else if (isPackFlow) void apply();
              else if (route) nav(route);
            }}
          >
            {applyLabel}
          </button>
        ) : null}
        <button
          type="button"
          className="flex min-h-11 items-center px-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
          disabled={busy}
          onClick={() => void dismiss()}
        >
          {t('Dismiss')}
        </button>
      </div>
    </div>
  );
}

/**
 * One task per row, with its photograph.
 *
 * The state is said in words rather than left to a colour, where a colour
 * could not carry it: "sent, not opened yet" is the fact that turns into a
 * phone call at eleven. Work already started says so with the pulsing dot and
 * gives the subline back to where the job is.
 */
function RunningRow({ op, person }: { op: Operation; person: StaffRow | null }) {
  const started = op.state === 'in_progress' || op.state === 'waiting_proof';
  const title = pickTranslation(op.titleTranslations, op.title);
  const stateWord =
    op.state === 'seen'
      ? t('Opened it')
      : op.state === 'scheduled'
        ? t('Scheduled')
        : op.state === 'sent'
          ? t('Sent, not opened yet')
          : op.state === 'waiting_proof'
            ? t('Waiting for proof')
            : t('Started');
  const subline = [
    person?.full_name ?? t('Unassigned'),
    op.locationText,
    op.state === 'in_progress' ? null : stateWord,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Link
      to={`/op/${op.id}`}
      className="group flex items-center rounded-[24px] border border-border bg-card p-4 shadow-[0_4px_20px_rgba(0,0,0,0.01)] transition-all active:scale-[0.99]"
    >
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-[18px] border border-black/[0.03] shadow-sm">
        <img src={opPhoto(title)} alt="" className="h-full w-full object-cover" loading="lazy" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col px-4 text-start">
        <div className="mb-1 flex items-center gap-2">
          <span className="truncate text-[14px] font-bold text-foreground" dir="auto">
            {title}
          </span>
          {started && (
            <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-primary" />
          )}
        </div>
        <span className="truncate text-[12px] font-medium tracking-tight text-muted-foreground">
          {subline}
        </span>
      </div>
      <Icon
        icon="solar:alt-arrow-right-bold"
        className="shrink-0 text-xl text-muted-foreground/30 transition-colors group-hover:text-muted-foreground rtl:-scale-x-100"
        aria-hidden
      />
    </Link>
  );
}

/**
 * The rest of the day, as a spine of dots. The next thing wears the orange
 * ring; everything after it is a plain hairline dot on the same line.
 */
function TimelineRow({
  item,
  isNext,
  isLast,
  staffById,
}: {
  item: PulseItem;
  isNext: boolean;
  isLast: boolean;
  staffById: Map<string, StaffRow>;
}) {
  const person = item.assignee_user_id ? staffById.get(item.assignee_user_id) : null;
  const to =
    item.kind === 'calendar'
      ? `/event/${item.id}`
      : item.kind === 'operation'
        ? `/op/${item.id}`
        : '/tasks';
  return (
    <div className="relative flex gap-6">
      {!isLast && <div className="absolute bottom-[-32px] start-[7px] top-[24px] w-px bg-border" />}
      <div
        className={`z-10 mt-1 h-3.5 w-3.5 shrink-0 rounded-full border-[1.5px] bg-card shadow-[0_0_0_4px_rgb(var(--background))] ${
          isNext ? 'border-primary' : 'border-border'
        }`}
      />
      <Link to={to} className="flex min-w-0 flex-col gap-0.5">
        {item.when && (
          <span className="text-[13px] font-bold text-foreground">{fmtRelative(item.when)}</span>
        )}
        <span className="text-[13px] font-medium tracking-tight text-muted-foreground" dir="auto">
          {pickTranslation(item.title_translations, item.title)}
        </span>
        {person && (
          <span className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground/70">
            {person.full_name}
          </span>
        )}
      </Link>
    </div>
  );
}

/** The things he goes to when nothing is asking for him. An amount only when
 *  there is one — a "0" here is the same noise the tiles were. */
function QuietLinks({ monthly }: { monthly: MonthlyExpensesSnap | null }) {
  const spend =
    monthly && monthly.recent.length > 0 ? fmtMoney(monthly.total_minor, monthly.currency) : null;
  return (
    <section className="flex items-center justify-between border-t border-border px-1 pb-10 pt-10">
      <Link
        to="/memory"
        className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
      >
        {t('House rules')}
      </Link>
      <Link
        to="/staff"
        className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
      >
        {t('People')}
      </Link>
      <Link
        to="/money"
        className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
      >
        {spend ? `${t('Money')} · ${spend}` : t('Money')}
      </Link>
      <Link
        to="/pulse"
        className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
      >
        {t('What’s coming')}
      </Link>
    </section>
  );
}

/** Only ever seen once, by a household with nothing in it yet. */
function Quickstart() {
  const cards: { to: string; title: string; icon: string }[] = [
    { to: '/staff', title: t('Add your first staff member'), icon: 'ph:user-plus-bold' },
    { to: '/tasks?new=1', title: t('Create first household task'), icon: 'ph:sparkle-fill' },
    { to: '/routines', title: t('Add a routine'), icon: 'ph:clock-counter-clockwise-bold' },
    { to: '/memory', title: t('Add a rule'), icon: 'ph:book-open-bold' },
  ];
  return (
    <section className="mb-12 flex flex-col gap-4">
      <h2 className="section-title mb-0 px-1">{t('Quickstart')}</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {cards.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="flex items-center gap-4 rounded-[24px] border border-border bg-card p-6 shadow-[0_8px_30px_rgba(0,0,0,0.02)] transition-all active:scale-[0.99]"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-secondary">
              <Icon icon={c.icon} className="text-2xl" aria-hidden />
            </span>
            <span className="font-bold leading-5">{c.title}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
